import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "yaprio-theme";
const VALID = ["light", "dark", "system"];

function systemDark() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolvedTheme(pref) {
  if (pref === "system") return systemDark() ? "dark" : "light";
  return pref;
}

function apply(pref) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const t = resolvedTheme(pref);
  root.classList.remove("light", "dark");
  root.classList.add(t);
  root.setAttribute("data-theme", t);
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    if (typeof window === "undefined") return "light";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID.includes(stored) ? stored : "light";
  });

  useEffect(() => {
    apply(preference);
  }, [preference]);

  // Respond to system-theme changes when preference is "system".
  useEffect(() => {
    if (preference !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  const setTheme = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setPreference(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_e) {
      /* ignore storage errors (private mode etc.) */
    }
  }, []);

  const value = useMemo(
    () => ({ preference, theme: resolvedTheme(preference), setTheme }),
    [preference, setTheme]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext) || { preference: "light", theme: "light", setTheme: () => {} };
}
